import { apiConfig, getToken } from '../config/api'
import { adaptSseEventToTracePatch } from '../adapters/assistantTrace.adapter'
import { ResponseStreamParser } from '@/utils/ResponseStreamParser'
import { parseResponseWrapper } from '@/utils/AIGuidance'

export async function streamMessage(body, options) {
  const { onToken, onDone, onError, onTracePatch, signal } = options
  const tokenObj = getToken()
  const baseUrl = apiConfig.baseUrl || ''
  const url = `${baseUrl.replace(/\/$/, '')}${apiConfig.conversationsEndpoint}`

  const decoder = new TextDecoder()
  let buffer = ''

  const parser = new ResponseStreamParser()
  let emittedPreambleLength = 0
  let structuredThrottleTimer = 0
  let pendingStructuredPatch = null

  function emitPreambleDelta(snapshot) {
    if (!onToken) return
    const currentPreamble = snapshot.preamble || ''
    if (currentPreamble.length > emittedPreambleLength) {
      const delta = currentPreamble.slice(emittedPreambleLength)
      onToken(delta)
      emittedPreambleLength = currentPreamble.length
    }
  }

  function flushPendingStructuredPatch() {
    structuredThrottleTimer = 0
    if (!pendingStructuredPatch || !onTracePatch) return
    onTracePatch(pendingStructuredPatch)
    pendingStructuredPatch = null
  }

  function emitStructuredResponseSnapshot(snapshot) {
    if (!onTracePatch) return
    if (snapshot.phase !== 'streaming' && snapshot.phase !== 'done') return

    const patch = {
      kind: 'structured_response',
      phase: snapshot.phase === 'done' ? 'end' : 'streaming',
      title: '',
      content: snapshot.response.message,
      status: snapshot.phase === 'done' ? 'completed' : 'streaming',
      isStructuredResponse: true,
      isIncremental: snapshot.phase === 'streaming',
      responseType: snapshot.response.type,
      options: snapshot.response.options,
      doctors: snapshot.response.doctors,
      packages: snapshot.response.packages,
      next_actions: snapshot.response.next_actions,
      strict: snapshot.strict,
      error: snapshot.error,
    }

    if (snapshot.phase === 'done') {
      // 最终快照必须立即发出，并清空可能存在的 pending。
      if (structuredThrottleTimer) {
        clearTimeout(structuredThrottleTimer)
        structuredThrottleTimer = 0
      }
      pendingStructuredPatch = null
      onTracePatch(patch)
      return
    }

    // streaming 阶段对 doctors/options 的增量更新做节流，
    // 避免每个 chunk 都触发 Vue 重绘和滚动调整，降低抽动频率。
    pendingStructuredPatch = patch
    if (!structuredThrottleTimer) {
      structuredThrottleTimer = setTimeout(flushPendingStructuredPatch, 120)
    }
  }

  function processSnapshot(snapshot) {
    // 无论当前处于哪个阶段，都先把已经确定的 preamble 文本发出。
    emitPreambleDelta(snapshot)

    // 进入 <RESPONSE> 后，通过结构化快照实时输出内部字段增量。
    emitStructuredResponseSnapshot(snapshot)
  }

  function emitFallbackForMalformedResponse(rawText) {
    if (!onTracePatch) return
    const fallback = parseResponseWrapper(rawText)
    if (!fallback) return
    onTracePatch({
      kind: 'structured_response',
      phase: 'end',
      title: '',
      content: fallback.message,
      status: 'completed',
      isStructuredResponse: true,
      responseType: fallback.type,
      options: fallback.options,
      doctors: fallback.doctors,
      packages: fallback.packages,
      next_actions: fallback.next_actions,
    })
  }

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': tokenObj ? `Bearer ${tokenObj}` : '',
      },
      body: JSON.stringify(body),
      signal,
    })

    if (resp.status === 401) {
      // reaouth() // 触发全局的登录失效逻辑
      return
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => '')
      throw new Error(text ? `请求失败(${resp.status}): ${text}` : `请求失败(${resp.status})`)
    }

    const reader = resp.body && resp.body.getReader ? resp.body.getReader() : null
    if (!reader) {
      throw new Error('响应不支持流式读取')
    }

    let raw = ''

    let done = false
    while (!done) {
      const readResult = await reader.read()
      done = Boolean(readResult.done)
      const value = readResult.value
      if (done) break
      if (!value) continue

      const chunk = decoder.decode(value, { stream: true })
      raw += chunk
      buffer += chunk

      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith('data:')) continue
        const payloadText = trimmed.slice(5).trim()
        if (!payloadText) continue

        let event
        try {
          event = JSON.parse(payloadText)
        } catch {
          continue
        }

        // console.log('[stream] parsed event:', event)

        const patch = onTracePatch ? adaptSseEventToTracePatch(event) : null
        if (patch && onTracePatch) {
          // console.log('[stream] trace patch:', patch)
          onTracePatch(patch)
        }

        if (event && event.type === 'stream' && typeof event.message === 'string') {
          // console.log('[stream] token:', event.message)
          const snapshot = parser.push(event.message)
          processSnapshot(snapshot)
        }
        if (event && event.type === 'error') {
          const msg = typeof event.message === 'string' && event.message ? event.message : '服务异常'
          throw new Error(msg)
        }
      }
    }

    if (buffer.trim().startsWith('data:')) {
      const payloadText = buffer.trim().slice(5).trim()
      try {
        const event = JSON.parse(payloadText)
        const patch = onTracePatch ? adaptSseEventToTracePatch(event) : null
        if (patch && onTracePatch) {
          onTracePatch(patch)
        }
        if (event && event.type === 'stream' && typeof event.message === 'string') {
          parser.push(event.message)
        }
      } catch (e) {
        // ignore
      }
    }

    const finalSnapshot = parser.finish(decoder.decode())
    processSnapshot(finalSnapshot)

    // 如果最终 JSON 严格校验失败，使用原有的 sanitize 解析作为兜底，兼容 LLM 输出中的未转义引号等情况。
    if (!finalSnapshot.strict && finalSnapshot.error) {
      emitFallbackForMalformedResponse(finalSnapshot.rawText)
    }

    if (onDone) onDone(raw)
  } catch (error) {
    if (error && error.name === 'AbortError') {
      // 用户主动中断，不作为错误处理
      if (onDone) onDone('')
      return
    }
    if (onError) onError(error)
  }
}
